import React, { useState, useMemo, useRef } from "react";
import { IReviewDetailResponse } from "interfaces/IReviews";
import "./ProductReviews.scss";

interface ProductReviewsProps {
  reviews: IReviewDetailResponse[];
  productName: string;
}

const ProductReviews: React.FC<ProductReviewsProps> = ({
  reviews,
  productName,
}) => {
  const [selectedRating, setSelectedRating] = useState<number | null>(null);
  const [sortOrder, setSortOrder] = useState<"recent" | "oldest">("recent");
  const [startIndex, setStartIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const reviewsRef = useRef<HTMLDivElement>(null);

  // Calcular estadísticas de calificaciones
  const ratingStats = useMemo(() => {
    const stats = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach((review) => {
      stats[review.qualification as keyof typeof stats]++;
    });
    return stats;
  }, [reviews]);

  // Filtrar y ordenar reviews según la calificación seleccionada y orden
  const filteredReviews = useMemo(() => {
    let filtered = reviews;

    if (selectedRating !== null) {
      filtered = reviews.filter(
        (review) => review.qualification === selectedRating
      );
    }

    // Ordenar por fecha
    const sorted = [...filtered].sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();

      if (sortOrder === "recent") {
        return dateB - dateA; // Más recientes primero
      } else {
        return dateA - dateB; // Más antiguos primero
      }
    });

    return sorted;
  }, [reviews, selectedRating, sortOrder]);

  // Obtener las reseñas visibles (máximo 5)
  const visibleReviews = useMemo(() => {
    return filteredReviews.slice(startIndex, startIndex + 5);
  }, [filteredReviews, startIndex]);

  // Resetear índice cuando cambien los filtros
  React.useEffect(() => {
    setStartIndex(0);
  }, [selectedRating, sortOrder]);

  // Función para renderizar estrellas
  const renderStars = (rating: number) => {
    return (
      <div className="review-stars">
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            className={`review-star${rating >= star ? " active" : ""}`}
          >
            ★
          </span>
        ))}
      </div>
    );
  };
  const renderSmallStars = (rating: number) => {
    return (
      <div className="review-stars-sm">
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            className={`review-star${rating >= star ? " active" : ""}`}
          >
            ★
          </span>
        ))}
      </div>
    );
  };

  // Función para formatear fecha
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Función para obtener iniciales del usuario
  const getUserInitials = (userName: string) => {
    return userName
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Función para hacer scroll hacia las reseñas
  const scrollToReviews = () => {
    if (reviewsRef.current) {
      reviewsRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  // Función para manejar navegación a la primera reseña
  const handleScrollToTop = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setStartIndex(0);
      setIsAnimating(false);
      scrollToReviews();
    }, 300);
  };

  // Función para navegar hacia adelante
  const handleLoadMore = () => {
    setStartIndex((prev) => prev + 5);
    setTimeout(() => {
      scrollToReviews();
    }, 100);
  };

  return (
    <div className="product-reviews-container">
      <div className="reviews-content">
        {/* Filtros de calificación */}
        <div className="rating-filters">
          <h3>Calificación</h3>
          {[5, 4, 3, 2, 1].map((rating) => (
            <div
              key={rating}
              className={`rating-filter-item${
                selectedRating === rating ? " active" : ""
              }`}
              onClick={() =>
                setSelectedRating(selectedRating === rating ? null : rating)
              }
            >
              <div className="rating-stars">{renderStars(rating)}</div>
              <span className="rating-count">
                {ratingStats[rating as keyof typeof ratingStats] === 1
                  ? `${ratingStats[rating as keyof typeof ratingStats]} Opinión`
                  : `${
                      ratingStats[rating as keyof typeof ratingStats]
                    } Opiniones`}
              </span>
            </div>
          ))}
        </div>

        {/* Lista de comentarios */}
        <div className="reviews-list" ref={reviewsRef}>
          <div className="comments-header">
            <h3>Comentarios</h3>
            <button
              className="sort-button"
              onClick={() =>
                setSortOrder(sortOrder === "recent" ? "oldest" : "recent")
              }
            >
              {sortOrder === "recent"
                ? "Mas recientes"
                : "Mas antiguas"}
            </button>
          </div>
          <div
            className={`reviews-container ${isAnimating ? "animating" : ""}`}
          >
            {visibleReviews.length > 0 ? (
              visibleReviews.map((review) => (
                <div key={review.id} className="review-item">
                  <div className="review-header">
                    <div className="user-info">
                      <div className="user-avatar">
                        {review.user_image_url ? (
                          <img
                            src={review.user_image_url}
                            alt={review.user_name}
                            onError={(e) => {
                              // Si la imagen falla, mostrar iniciales
                              const target = e.target as HTMLImageElement;
                              target.style.display = "none";
                              target.nextElementSibling?.classList.remove(
                                "fallback"
                              );
                            }}
                          />
                        ) : null}
                        <span
                          className={`user-initials${
                            !review.user_image_url ? "" : " fallback"
                          }`}
                        >
                          {getUserInitials(review.user_name)}
                        </span>
                      </div>
                      <div className="user-details">
                        <span className="user-name">{review.user_name}</span>
                        <div className="user-rating">
                          {renderSmallStars(review.qualification)}
                        </div>
                        <div className="review-meta">
                          <span className="review-date-label">
                            Comentado el {formatDate(review.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  {review.comment && (
                    <div className="review-comment">{review.comment}</div>
                  )}
                </div>
              ))
            ) : (
              <div className="no-reviews">
                {selectedRating
                  ? `No hay reseñas con ${selectedRating} estrellas`
                  : "No hay reseñas para este producto"}
              </div>
            )}
          </div>

          {/* Flecha hacia abajo para cargar más reseñas */}
          {filteredReviews.length > startIndex + 5 && (
            <div className="navigation-arrow down" onClick={handleLoadMore}>
              <svg
                width="50"
                height="20"
                viewBox="0 0 50 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="arrow-icon"
              >
                <path
                  d="M5 7L25 17L45 7"
                  stroke="#000"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          )}

          {/* Flecha hacia arriba para volver al inicio */}
          {filteredReviews.length > 5 && startIndex > 0 && (
            <div className="navigation-arrow up" onClick={handleScrollToTop}>
              <svg
                width="50"
                height="20"
                viewBox="0 0 50 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="arrow-icon"
              >
                <path
                  d="M5 13L25 3L45 13"
                  stroke="#000"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductReviews;
